import { Fragment, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ChallengeSection from './ChallengeSection'
import Callout from '../../components/Callout'
import VideoEmbed from '../../components/VideoEmbed'
import Label from '../../components/Label'
import SectionHeader from '../../components/SectionHeader'
import BottomDrawer from '../../components/BottomDrawer'
import Page from '../../components/Page'
import Prompt from '../../components/Prompt'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import FormControlLabel from '@mui/material/FormControlLabel'
import useQualtricsSurvey from '../../hooks/useQualtricsSurvey'
import ConflictCard from './ConflictCard'
import useContentful from '../../contexts/useContentful'
import { ReactComponent as IconConflict } from '../../icons/Conflict.svg'
import { ReactComponent as IconClose } from '../../icons/Close.svg'
import { ReactComponent as IconHeart } from '../../icons/Heart.svg'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { find, filter, some } from 'lodash'
import { Paper } from '@mui/material'
// import useRecaptcha from "../../hooks/useRecaptcha";

const MAPPED_LIKERT = {
  0: '5',
  1: '4',
  2: '3',
  3: '2',
  4: '1',
}

const NEXT_INDEX = {
  'housing': 3,
  'water': 4,
  'transportation': 5,
  'open-space-and-recreation': 6,
}

const NEXT_PAGE = {
  'housing': surveyID => `/topic/${surveyID}/water`,
  'water': surveyID => `/topic/${surveyID}/transportation`,
  'transportation': surveyID => `/topic/${surveyID}/open-space-and-recreation`,
  'open-space-and-recreation': surveyID => `/imagine/${surveyID}/1`,
}

const TopicPage = () => {
  const navigate = useNavigate()
  const { 
    submitMultipleChoiceAnswer, 
    advanceSurvey,
    qualtricsLoading,
  } = useQualtricsSurvey()
  const { pageID, surveyID } = useParams()
  const responses = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.responses)
  const qualtricsQuestions = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.qualtricsQuestions)
  const [value, setValue] = useState('')
  const {
    questions,
    theChallenge,
    other,
    buttonText,
    commentQualtricsId,
    preferenceQualtricsId,
    conflicts,
    visualizationText,
    videoId,
    conflictDrawer,
    optionLabel,
    conflictAlert,
    errorText,
    housingConflictLabel,
    housingConflictOptions,
    housingConflictButtonText,
  } = useContentful()
  const {
    solutionPreference = [],
  } = other?.[0]?.fields || {}
  const [foundConflicts, setFoundConflicts] = useState([])
  const [resolvedConflicts, setResolvedConflicts] = useState([])
  const [conflictDrawerOption, setConflictDrawerOption] = useState(null)
  const [visualizationDrawer, setVisualizationDrawer] = useState({})
  const [visualizationOpen, setVisualizationOpen] = useState(false)
  const [finalConflictDrawerOpen, setFinalConflictDrawerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tryAgainError, setTryAgainError] = useState(false)
  // const { executeRecaptcha } = useRecaptcha()

  const [
    housingConflictOptionsZipped,
  ] = useMemo(() => {
    return [
      find(qualtricsQuestions, { questionId: 'QID205' })?.choices?.map((o, idx) => ({ ...o, display: housingConflictOptions?.[idx] })) || [],
    ]
  }, [
    housingConflictOptions,
  ])

  const [
    isDrawerReady,
    flaggedOptions,
    shownActiveConflicts,
  ] = useMemo(() => {
    let tempIsDrawerReady = false
    let tempActiveConflicts = []
    let ct = 0
    let tempFlaggedOptions = []
    foundConflicts.forEach(o => {
      if (!resolvedConflicts.includes(o.qualtricsId)) {
        tempIsDrawerReady = true
        ct++
        tempActiveConflicts.push(o)
        tempFlaggedOptions = 
          tempFlaggedOptions.concat(
            o.conflictingOptions?.map?.(o1 => ({ 
              qualtricsId: o.qualtricsId, 
              option: o1.fields?.qualtricsId,
            })))
      }
    })
    return [
      tempIsDrawerReady,
      // ct,
      // tempActiveConflicts,
      tempFlaggedOptions,
      filter(tempActiveConflicts, o => some(o.conflictingOptions, option => option?.fields?.qualtricsId === conflictDrawerOption)),
    ]
  }, [foundConflicts, resolvedConflicts, conflictDrawerOption])

  const dismissConflict = conflicts => {
    setResolvedConflicts([...resolvedConflicts, ...conflicts?.map?.(o => o.qualtricsId)])
  }

  const handleSkip = conflict => {
    setResolvedConflicts([...resolvedConflicts, conflict?.qualtricsId])
  }

  const handleSkipActive = () => {
    setResolvedConflicts(shownActiveConflicts.map(o => o.qualtricsId))
  }

  const onSelect = (conflict, choiceId) => {
    submitMultipleChoiceAnswer(conflict?.qualtricsId, choiceId)
    setResolvedConflicts([...resolvedConflicts, conflict?.qualtricsId])
  }

  const advance = async () => {
    setIsLoading(true)
    // const actionName = `advance_topic_${pageID}`.replace(/-/g, '_')
    // const response = await executeRecaptcha(actionName)
    advanceSurvey({
      // score: response?.score,
      nextIndex: NEXT_INDEX[pageID],
      cb: error => {
        setIsLoading(false)
        if (!error) {
          setTryAgainError(false)
          navigate(NEXT_PAGE[pageID]?.(surveyID))
        } else {
          setTryAgainError(true)
        }
      },
    }, commentQualtricsId, value)
  }

  const onSubmit = bypass => {
    if (pageID === 'open-space-and-recreation' && filter([responses.QID80, responses.QID84, responses.QID88, responses.QID92], num => num === 4).length >= 2 && !bypass) {
      setFinalConflictDrawerOpen(true)
    } else {
      advance()
    }
  }

  useEffect(() => {
    if (!isDrawerReady && conflictDrawerOption) setConflictDrawerOption(null)
  }, [isDrawerReady, conflictDrawerOption])

  useEffect(() => {
    let temp = []
    conflicts?.forEach(({ fields }) => {
      if (
        responses[fields?.conflictingOptions?.[0]?.fields?.qualtricsId] === 0 && 
        responses[fields?.conflictingOptions?.[1]?.fields?.qualtricsId] === 0
      ) {
        temp.push(fields)
      }
    })
    setFoundConflicts(temp)
  }, [responses, conflicts])

  useEffect(() => {
    if (conflictDrawerOption) {
      let resolved = true
      foundConflicts?.forEach(o => {
        o.conflictingOptions?.forEach(({ fields }) => {
          if (fields.qualtricsId === conflictDrawerOption && !resolvedConflicts.includes(o.qualtricsId)) resolved = false
        })
      })
      if (resolved) setConflictDrawerOption(null)
    }
  }, [conflictDrawerOption, resolvedConflicts, foundConflicts])

  useEffect(() => {
    if (value.length) setValue('')
  }, [pageID])

  return (
    <Page>
      <Stack>
        <Container>
          <Stack
            display="grid"
            py={6}
            gap={4}
          >
            <SectionHeader />
            {!!videoId && (
              <VideoEmbed
                videoId={videoId}
              />
            )}
            <ChallengeSection
              description={documentToReactComponents(theChallenge)}
            />
            {questions?.map(({ 
              fields: { title, description, image, benefits, limitations, obstacles, qualtricsId, visualizationImage, visualizationTitle }
            }, idx) => (
              <Fragment
                key={title}
              >
                <Callout
                  label={`${optionLabel} ${idx + 1}`}
                  imageUrl={image?.fields?.file?.url}
                  title={title}
                  description={description}
                  benefits={benefits}
                  limitations={limitations}
                  obstacles={obstacles}
                  value={responses[qualtricsId]}
                  onChange={response => submitMultipleChoiceAnswer(qualtricsId, MAPPED_LIKERT[response], response)}
                  visualizationText={visualizationText}
                  visualizationTitle={visualizationTitle}
                  visualizationImage={visualizationImage}
                  onOpenVisualization={visualization => {
                    setVisualizationDrawer(visualization)
                    setVisualizationOpen(true)
                  }}
                />
                {find(flaggedOptions, { option: qualtricsId }) && (
                  <Paper
                    className="drawn"
                    sx={{
                      py: .1,
                    }}
                  >
                    <Stack
                      display="grid"
                      gap={1}
                      gridTemplateColumns="auto 1fr auto"
                      alignItems="center"
                      alignContent="center"
                      py={1.5}
                      px={2.5}
                      backgroundColor="white"
                      sx={{
                        borderRadius: '20px',
                      }}
                    >
                      <IconConflict />
                      <Stack
                        display="inline-grid"
                        justifyContent="flex-start"
                        gridAutoFlow={{
                          sm: 'column',
                        }}
                        gap={2}
                        alignItems="center"
                        alignContent="center"
                      >
                        <Typography
                          variant="body1Semibold"
                        >
                          {conflictAlert?.[0]}
                        </Typography>
                        <Typography 
                          variant="link"
                          color="secondary.main"
                          sx={{
                            cursor: 'pointer',
                          }}
                          onClick={() => setConflictDrawerOption(qualtricsId)}
                        >
                          {conflictAlert?.[1]}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          cursor: 'pointer',
                        }}
                        onClick={() => dismissConflict(filter(flaggedOptions, { option: qualtricsId }))}
                      >
                        <IconClose />
                      </Box>
                    </Stack>
                  </Paper>
                )}
                {idx !== questions.length - 1 && (
                  <Divider />
                )}
              </Fragment>
            ))}
            <Stack>
              <Typography
                variant="h4Bold"
                mb={3}
              >
                {solutionPreference[0]}
              </Typography>
              <Stack
                display="grid"
                gridTemplateColumns={{
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                }}
                gap={2}
                mb={4}
              >
                {questions?.map((o, idx) => (
                  <Stack
                    key={`option${idx}`}
                    display="inline-grid"
                    gridTemplateRows="1fr auto"
                    gap={1.5}
                  >
                    <Paper
                      variant="outlined"
                      sx={theme => ({
                        display: 'inline-grid',
                        borderRadius: '8px',
                        borderWidth: '2px',
                        cursor: 'pointer',
                        borderColor: idx + 1 === responses[preferenceQualtricsId] ? 'primary.light' : undefined,
                        bgcolor: idx + 1 === responses[preferenceQualtricsId] ? 'primary.lightest' : 'nuetral.lightest',
                        transition: theme.transitions.create(['border-color']),
                        '&:hover .hover': {
                          bgcolor: 'primary.light',
                        },
                        '&:hover': {
                          borderColor: 'primary.light',
                        }
                      })}
                      onClick={() => submitMultipleChoiceAnswer(preferenceQualtricsId, (idx + 1))}
                    >
                      <Stack
                        display="grid"
                        gridTemplateRows="1fr auto"
                        gap={1.5}
                        justifyItems="center"
                        justifyContent="center"
                        px={1.5}
                        py={2.5}
                        textAlign="center"
                      >
                        <Stack
                          display="grid"
                          gap={1.5}
                          justifyItems="center"
                          justifyContent="center"
                          alignContent="flex-start"
                        >
                          <Typography
                            variant="body1Semibold"
                          >
                            {optionLabel}{' '}{idx + 1}
                          </Typography>
                          <Box
                            sx={{
                              width: 150,
                              height: 150,
                            }}
                          >
                            <img 
                              src={o.fields?.image?.fields?.file?.url}
                              alt={o.fields?.image?.fields?.title}
                            />
                          </Box>
                          <Typography
                            variant="body1"
                            color="text.neutralDark"
                          >
                            {' '}{o.fields?.title}
                          </Typography>
                        </Stack>
                        <Box
                          className="hover"
                          sx={theme => ({
                            alignSelf: 'flex-end',
                            justifySelf: 'center',
                            display: 'inline-grid',
                            placeItems: 'center',
                            placeContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: 32,
                            bgcolor: idx + 1 === responses[preferenceQualtricsId] ? 'primary.light' : 'primary.lightest',
                            transition: theme.transitions.create(['background-color']),
                          })}
                        >
                          <IconHeart
                            width="32"
                            height="32"
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                ))}
              </Stack>
              <Stack
                gap={1}
              >
                <Label>
                  {solutionPreference[1]}
                </Label>
                <TextField
                  multiline
                  rows={3}
                  placeholder={solutionPreference[2]}
                  value={value}
                  onChange={e => {
                    setValue(e.target.value)
                    // debouncedSubmitTextAnswer(commentQualtricsId, e.target.value)
                  }}
                />
              </Stack>
            </Stack>
            <Stack
              display="grid"
              justifyContent="center"
            >
              <Button
                sx={{
                  position: 'relative',
                }}
                disabled={isLoading || qualtricsLoading}
                onClick={() => onSubmit()}
              >
                <span
                  style={{
                    opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                  }}
                >
                  {buttonText}
                </span>
                {(isLoading || qualtricsLoading) && (
                  <CircularProgress 
                    size={25}
                    sx={{
                      position: 'absolute',
                    }}
                  />
                )}
              </Button>
              {tryAgainError && (
                <Typography
                  variant="body1"
                  color="error"
                  mt={2}
                  textAlign="center"
                >
                  {errorText}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Container>

        {
        //
        // Conflict Drawer
        //
        }
        <BottomDrawer
          title={(
            <Stack
              gap={1}
            >
              <Typography
                variant="bigLabel"
                color="text.neutralDark"
              >
                {visualizationDrawer?.text}
              </Typography>
            </Stack>
          )}
          open={visualizationOpen}
          onClose={() => setVisualizationOpen(false)}
        >
          <Stack>
            <img
              src={visualizationDrawer?.image?.fields?.file?.url} 
              alt={visualizationDrawer?.image?.fields?.title}
            />
          </Stack>
        </BottomDrawer>
        <BottomDrawer
          title={<div />}
          open={!!conflictDrawerOption?.length}
          onClose={handleSkipActive}
        >
          {foundConflicts?.map((o, idx) => (
            <Collapse 
              in={!resolvedConflicts.includes(o.qualtricsId) && find(o.conflictingOptions?.map(o1 => o1.fields), { qualtricsId: conflictDrawerOption })} 
              key={o.qualtricsId}
            >
              <Stack
                sx={{
                  borderBottomStyle: 'solid',
                  borderBottomWidth: 1,
                  borderBottomColor: 'neutral.lighter',
                  pb: 6,
                  mb: 6,
                }}
              >
                <Stack
                  display="grid"
                >
                  <Typography
                    variant="bigLabel"
                    color="text.neutralDark"
                    maxWidth="700px"
                    textAlign="center"
                    mx="auto"
                    mb={4}
                  >
                    {o?.conflictExplanation?.fields?.explanationText}
                  </Typography>
                  <Stack
                    display="grid"
                    gap={{
                      xs: 1.5,
                      sm: 4,
                      md: 8,
                    }}
                    gridAutoFlow="column"
                    justifyContent="center"
                    mb={2}
                  >
                    <ConflictCard
                      {...o?.conflictingOptions?.[0]?.fields}
                      optionLabel={optionLabel}
                      selectionLabel={conflictDrawer?.[3]}
                      onSelect={() => onSelect(o, '1')}
                    />
                    <Typography
                      variant="subtitle1"
                      textAlign="center"
                      sx={{
                        display: {
                          xs: 'none',
                          sm: 'initial',
                        },
                        alignSelf: 'center',
                      }}
                    >
                      {conflictDrawer?.[1]}
                    </Typography>
                    <ConflictCard
                      {...o?.conflictingOptions?.[1]?.fields}
                      optionLabel={optionLabel}
                      selectionLabel={conflictDrawer?.[3]}
                      onSelect={() => onSelect(o, '2')}
                    />
                  </Stack>
                  <Button
                    className="light"
                    size="small"
                    sx={{
                      justifySelf: 'center',
                    }}
                    onClick={() => handleSkip(o)}
                  >
                    {conflictDrawer?.[2]}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          ))}
        </BottomDrawer>
        <BottomDrawer
          hideClose
          title={(
            <Stack
              gap={4}
              maxWidth="785px"
            >
              {housingConflictLabel?.map?.(o => (
                <Typography
                  key={o}
                  variant="bigLabel"
                  color="text.neutralDark"
                >
                  {o}
                </Typography>
              ))}
            </Stack>
          )}
          open={finalConflictDrawerOpen}
          // onClose={() => setFinalConflictDrawerOpen(false)}
        >
          <Stack
            gap={8}
          >
            <RadioGroup
              aria-label="options"
              sx={{
                gap: '40px !important',
              }}
              value={responses.QID205}
              onChange={e => submitMultipleChoiceAnswer('QID205', e.target.value)}
            >
              {housingConflictOptionsZipped?.map(o => (
                <FormControlLabel
                  key={o.display}
                  value={o.choiceId}
                  control={<Radio size="medium" />}
                  label={o.display}
                />
              ))}
            </RadioGroup>
            <Stack
              display="grid"
              justifyContent="center"
            >
              <Button
                sx={{
                  position: 'relative',
                }}
                disabled={isLoading || qualtricsLoading}
                onClick={() => onSubmit(true)}
              >
                <span
                  style={{
                    opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                  }}
                >
                  {housingConflictButtonText}
                </span>
                {(isLoading || qualtricsLoading) && (
                  <CircularProgress 
                    size={25}
                    sx={{
                      position: 'absolute',
                    }}
                  />
                )}
              </Button>
            </Stack>
          </Stack>
        </BottomDrawer>
      </Stack>
      <Prompt />
    </Page>
  )
}

export default TopicPage
